import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

// Same rules as backend's subscription_tier_of() — legacy "pro"
// counts as pro_global; admin counts as everything.
const PRO_TIERS = new Set(["pro", "pro_local", "pro_global"]);
function isPro(u) {
    return !!u && (u.role === "admin" || PRO_TIERS.has(u.subscription));
}

/**
 * Reusable hook for "message this user" interactions across the app
 * (post cards, comment author names, profile pages, etc.).
 *
 * Returns [openMessageFor, proModalOpen, setProModalOpen]
 *   - openMessageFor(targetUserId, opts?): fires the flow.
 *       opts.name          → recipient display name (for toasts/log)
 *       opts.opener        → optional opening line for the DM
 *   - Free users: opens the Pro upgrade modal (parent renders it).
 *   - Not logged in: routes to /auth.
 *   - Blocked recipient / disabled DMs: shows friendly toast.
 */
export function useMessageUser() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [proModalOpen, setProModalOpen] = useState(false);
    const [busy, setBusy] = useState(false);

    const openMessageFor = useCallback(async (targetUserId, opts = {}) => {
        if (!targetUserId) return;
        if (!user) {
            navigate("/auth");
            return;
        }
        if (user.id === targetUserId) return;   // self-message no-op
        if (!isPro(user)) {
            setProModalOpen(true);
            return;
        }
        if (busy) return;
        setBusy(true);
        try {
            const opener = opts.opener || `Hi ${opts.name || "there"} 👋`;
            const { data } = await api.post("/messages", {
                recipient_id: targetUserId,
                body: opener,
            });
            navigate(`/messages/${data.conversation_id}`);
        } catch (e) {
            const msg = e?.response?.data?.detail || "Could not start conversation";
            toast.error(typeof msg === "string" ? msg : "Could not start conversation");
        } finally {
            setBusy(false);
        }
    }, [user, navigate, busy]);

    return { openMessageFor, proModalOpen, setProModalOpen, isPro: isPro(user) };
}
